<template>
  <section class="wa-list" :class="{ 'wa-list--mobile': isMobileViewport }">
    <div v-if="!workingAreas.length" class="wa-empty">
      <p>{{ $t('no_working_areas') }}</p>
    </div>

    <div v-else class="wa-panel">
      <input
        v-model="areaSearch"
        type="search"
        class="wa-search"
        :placeholder="$t('search')"
        autocomplete="off"
      />

      <div v-if="selectedCount" class="wa-selection-bar">
        <span class="wa-selection-count">{{ $t('n_selected', { count: selectedCount }) }}</span>
        <div class="wa-selection-actions">
          <button
            v-if="isAdmin"
            type="button"
            class="wa-selection-btn wa-selection-btn--primary"
            @click="$emit('bulkEdit', selectedIds)"
          >
            {{ $t('wa_bulk_edit') }}
          </button>
          <button type="button" class="wa-selection-btn" @click="selectAllFiltered">
            {{ $t('select_all') }}
          </button>
          <button type="button" class="wa-selection-btn" @click="clearSelection">
            {{ $t('clear_selection') }}
          </button>
        </div>
      </div>

      <div v-if="!filteredWorkingAreas.length" class="wa-empty">
        <p>{{ $t('no_results_found') }}</p>
      </div>

      <div v-else-if="!isMobileViewport" class="wa-table-wrap">
        <table class="wa-table">
          <colgroup>
            <col class="wa-col-check" />
            <col class="wa-col-area" />
            <col class="wa-col-status" />
            <col class="wa-col-rooms" />
            <col class="wa-col-shifts" />
            <col class="wa-col-alerts" />
            <col v-if="isAdmin" class="wa-col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th class="wa-th-check">
                <Checkbox
                  :value="areAllFilteredSelected"
                  @input="toggleSelectAllFiltered"
                />
              </th>
              <th>{{ $t('area_name') }}</th>
              <th>{{ $t('status') }}</th>
              <th>{{ $t('rooms') }}</th>
              <th>{{ $t('shifts') || 'Shifts' }}</th>
              <th>{{ $t('working_area_alerts') }}</th>
              <th v-if="isAdmin" class="wa-th-actions">{{ $t('actions') || 'Actions' }}</th>
            </tr>
          </thead>
          <tbody>
            <WorkingAreaItem
              v-for="workingArea in filteredWorkingAreas"
              :key="workingArea.id"
              :working-area="workingArea"
              :is-admin="isAdmin"
              :selected="isSelected(workingArea.id)"
              @select="setSelected"
              @editWorkingArea="emitEdit"
              @removeWorkingArea="emitRemove"
              @openListModal="openListModal"
            />
          </tbody>
        </table>
      </div>

      <div v-else class="wa-mobile-list">
        <WorkingAreaMobileCard
          v-for="workingArea in filteredWorkingAreas"
          :key="workingArea.id"
          :working-area="workingArea"
          :is-admin="isAdmin"
          :selected="isSelected(workingArea.id)"
          @select="setSelected"
          @editWorkingArea="emitEdit"
          @removeWorkingArea="emitRemove"
          @openListModal="openListModal"
        />
      </div>
    </div>

    <WorkingAreaListModal
      v-if="listModal"
      :title-key="listModal.titleKey"
      :subtitle="listModal.subtitle"
      :items="listModal.items"
      @close="listModal = null"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import Checkbox from '@/modules/common/components/Checkbox'
import WorkingAreaItem from './WorkingAreaItem'
import WorkingAreaMobileCard from './WorkingAreaMobileCard.vue'
import WorkingAreaListModal from './WorkingAreaListModal.vue'

const MOBILE_BREAKPOINT = '(max-width: 768px)'

export default {
  props: {
    workingAreas: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      listModal: null,
      areaSearch: '',
      selectedIds: [],
      isMobileViewport: false,
      mobileMediaQuery: null,
      onMobileMediaQueryChange: null,
    }
  },
  computed: {
    ...mapGetters(['loggedUser']),
    isAdmin() {
      return this.loggedUser && this.loggedUser.type === 'admin'
    },
    filteredWorkingAreas() {
      const query = this.normalizedSearch
      if (!query) return this.workingAreas || []
      return (this.workingAreas || []).filter((area) => this.areaMatchesSearch(area, query))
    },
    normalizedSearch() {
      return String(this.areaSearch || '').trim().toLowerCase()
    },
    selectedIdSet() {
      return new Set(this.selectedIds.map((id) => String(id)))
    },
    selectedCount() {
      return this.selectedIds.length
    },
    areAllFilteredSelected() {
      const filtered = this.filteredWorkingAreas
      if (!filtered.length) return false
      return filtered.every((area) => this.selectedIdSet.has(String(area.id)))
    },
  },
  watch: {
    workingAreas() {
      const validIds = new Set((this.workingAreas || []).map((area) => String(area.id)))
      this.selectedIds = this.selectedIds.filter((id) => validIds.has(String(id)))
    },
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this.mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT)
    this.isMobileViewport = this.mobileMediaQuery.matches
    this.onMobileMediaQueryChange = (event) => {
      this.isMobileViewport = event.matches
    }
    if (this.mobileMediaQuery.addEventListener) {
      this.mobileMediaQuery.addEventListener('change', this.onMobileMediaQueryChange)
    } else {
      this.mobileMediaQuery.addListener(this.onMobileMediaQueryChange)
    }
  },
  beforeDestroy() {
    if (!this.mobileMediaQuery || !this.onMobileMediaQueryChange) return
    if (this.mobileMediaQuery.removeEventListener) {
      this.mobileMediaQuery.removeEventListener('change', this.onMobileMediaQueryChange)
    } else {
      this.mobileMediaQuery.removeListener(this.onMobileMediaQueryChange)
    }
  },
  methods: {
    areaMatchesSearch(area, query) {
      if (!area) return false
      const fields = [
        area.area_name,
        area.name,
        ...(area.rooms_names || []),
        ...(area.shifts || []),
      ]
      return fields.some((field) => String(field || '').toLowerCase().includes(query))
    },
    isSelected(id) {
      return this.selectedIdSet.has(String(id))
    },
    setSelected(id, selected) {
      const key = String(id)
      const next = this.selectedIds.filter((existing) => String(existing) !== key)
      if (selected) next.push(id)
      this.selectedIds = next
    },
    toggleSelectAllFiltered(checked) {
      if (checked) this.selectAllFiltered()
      else this.deselectFiltered()
    },
    selectAllFiltered() {
      const filteredIds = this.filteredWorkingAreas.map((area) => area.id)
      const filteredKeys = new Set(filteredIds.map((id) => String(id)))
      const kept = this.selectedIds.filter((id) => !filteredKeys.has(String(id)))
      this.selectedIds = [...kept, ...filteredIds]
    },
    deselectFiltered() {
      const filteredKeys = new Set(this.filteredWorkingAreas.map((area) => String(area.id)))
      this.selectedIds = this.selectedIds.filter((id) => !filteredKeys.has(String(id)))
    },
    clearSelection() {
      this.selectedIds = []
    },
    openListModal(payload) {
      this.listModal = payload
    },
    emitEdit(workingAreaId) {
      this.$emit('editWorkingArea', workingAreaId)
    },
    emitRemove(workingAreaId) {
      this.$emit('removeWorkingArea', workingAreaId)
    },
  },
  components: { Checkbox, WorkingAreaItem, WorkingAreaMobileCard, WorkingAreaListModal },
}
</script>

<style lang="scss">
@use "../styles/workingAreaShared.scss";

.wa-panel {
  width: 100%;
}

.wa-search {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
  margin: 0 0 8px;
  min-height: 36px;
  height: 36px;
  padding: 6px 12px;
  border: 1px solid #d5dbe3;
  border-radius: 8px;
  font-size: 13px;
  color: #2c3e50;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #448aff;
    box-shadow: 0 0 0 3px rgba(68, 138, 255, 0.15);
  }
}

.wa-selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f3f7fd;
  border: 1px solid #d6e4f7;
}

.wa-selection-count {
  font-size: 12px;
  font-weight: 600;
  color: #2f5ea8;
}

.wa-selection-actions {
  display: flex;
  gap: 8px;
}

.wa-selection-btn {
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #2f5ea8;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: rgba(47, 94, 168, 0.08);
  }

  &--primary {
    background: #007bff;
    color: #fff;

    &:hover {
      background: #0056b3;
    }
  }
}

.wa-list {
  padding: 0 24px 24px;

  @media (max-width: 768px) {
    padding: 0 12px 16px;
  }
}

.wa-list--mobile {
  padding-top: 4px;
}

.wa-empty {
  padding: 32px 24px;
  text-align: center;
  color: #7a8490;
  font-size: 14px;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
}

.wa-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e6eaef;
  border-radius: 10px;
  background: #fff;
  direction: inherit;
  -webkit-overflow-scrolling: touch;
}

.wa-table {
  width: 100%;
  min-width: 780px;
  border-collapse: collapse;
  table-layout: fixed;

  .wa-col-check { width: 40px; }
  .wa-col-area { width: 26%; }
  .wa-col-status { width: 110px; }
  .wa-col-rooms { width: 72px; }
  .wa-col-shifts { width: 22%; }
  .wa-col-alerts { width: 28%; }
  .wa-col-actions { width: 72px; }

  thead tr {
    background: #f7f9fb;
    border-bottom: 1px solid #e6eaef;
  }

  th {
    padding: 8px 10px;
    text-align: start;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8a939e;
    white-space: nowrap;

    &.wa-th-actions {
      text-align: center;
    }

    &.wa-th-check {
      width: 40px;
      padding: 8px 6px;
      text-align: center;
      vertical-align: middle;
      overflow: hidden;
    }
  }

  tbody td {
    text-align: start;
  }

  .wa-cell--actions {
    text-align: center;
  }

  .wa-cell--status,
  .wa-cell--rooms {
    text-align: start;
  }

  .wa-cell--check {
    width: 40px;
    padding: 8px 6px;
    text-align: center;
    vertical-align: middle;
  }

  tbody tr {
    border-bottom: 1px solid #eef1f4;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #fbfcfd;
    }

    &.wa-row--active {
      background: #f7fbf8;
    }

    &.wa-row--inactive {
      background: #fafbfc;
    }

    &.wa-row--active:hover,
    &.wa-row--inactive:hover {
      filter: brightness(0.985);
    }
  }
}

.wa-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
